import { useCallback, useRef, useState } from 'react'
import { label } from './inbox'
import { store, type AccountInfo } from './store'
import { Tile } from './visuals'

type Status = 'checking' | 'done' | 'failed'
interface Row { account: AccountInfo; status: Status; fresh: number | null }

/**
 * r: check every mailbox for new mail now. Each account syncs on its own, so
 * the panel shows them finishing one by one, then how many new messages each
 * brought in.
 */
export function useRefresher() {
  const [rows, setRows] = useState<Row[] | null>(null)
  const [leaving, setLeaving] = useState(false)
  const running = useRef(false)
  const timer = useRef(0)

  const run = useCallback(async () => {
    if (running.current) return
    const accounts = store.get().accounts.filter(account => account.status !== 'disconnected')
    if (!accounts.length) return
    running.current = true
    clearTimeout(timer.current)
    setLeaving(false)
    setRows(accounts.map(account => ({ account, status: 'checking', fresh: null })))
    // Which messages each account already shows, to count what's new afterwards.
    const before = new Set(store.get().conversations.flatMap(item => item.messages.map(message => message.id)))
    const started = Date.now()
    await Promise.all(accounts.map(async account => {
      let ok = false
      try {
        const response = await fetch('/api/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accountId: account.id }) })
        ok = response.ok && (await response.json() as { ok?: boolean }).ok !== false
      } catch { /* shown as failed */ }
      // Let the spinner be seen even when a sync is instant.
      const wait = 450 - (Date.now() - started)
      if (wait > 0) await new Promise(resolve => setTimeout(resolve, wait))
      setRows(previous => previous?.map(row => row.account.id === account.id ? { ...row, status: ok ? 'done' : 'failed' } : row) ?? null)
    }))
    await store.refresh()
    const counts = new Map<string, number>()
    for (const item of store.get().conversations) for (const message of item.messages) {
      if (!before.has(message.id) && message.folder !== 'sent') counts.set(item.sourceId, (counts.get(item.sourceId) ?? 0) + 1)
    }
    setRows(previous => previous?.map(row => ({ ...row, fresh: counts.get(row.account.id) ?? 0 })) ?? null)
    running.current = false
    timer.current = window.setTimeout(() => {
      setLeaving(true)
      timer.current = window.setTimeout(() => { setRows(null); setLeaving(false) }, 220)
    }, 1600)
  }, [])

  return { rows, leaving, run }
}

export function RefreshPanel({ rows, leaving }: { rows: Row[] | null; leaving: boolean }) {
  if (!rows) return null
  const accounts = rows.map(row => row.account)
  return (
    <div className={`refresh-panel${leaving ? ' leaving' : ''}`} role="status" aria-live="polite">
      {rows.map((row, index) => (
        <div key={row.account.id} className={`refresh-row ${row.status}`} style={{ animationDelay: `${index * 45}ms` }}>
          <span className="refresh-tile">
            <svg className="refresh-ring" viewBox="0 0 28 28" aria-hidden="true"><circle cx="14" cy="14" r="12.5" pathLength="100" /></svg>
            <Tile brand={row.account.brand} tone={row.account.tone} size={18} />
          </span>
          <span className="refresh-name">{label(row.account, accounts)}</span>
          <span className="refresh-state" key={`${row.status}-${row.fresh ?? ''}`}>
            {row.status === 'checking' ? 'Checking…'
              : row.status === 'failed' ? 'Couldn’t reach'
              : row.fresh === null ? <Check />
              : row.fresh ? <strong>{row.fresh} new</strong> : 'Up to date'}
          </span>
        </div>
      ))}
    </div>
  )
}

function Check() {
  return <svg className="refresh-check" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M2.5 7.5l3 3 6-7" pathLength="100" /></svg>
}
